import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const VIDEO_DESKTOP = `${process.env.PUBLIC_URL}/assets/video/intro.mp4`;
const VIDEO_MOBILE = `${process.env.PUBLIC_URL}/assets/video/intro-mobile.mp4`;

function pickVideoSrc() {
  if (typeof window === 'undefined') return VIDEO_DESKTOP;
  const isMobileSize = window.innerWidth <= 767 || window.matchMedia('(max-width: 767px)').matches;
  const isMobileUA = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  return (isMobileSize || isMobileUA) ? VIDEO_MOBILE : VIDEO_DESKTOP;
}

const HeroVideo = () => {
  const sectionNodeRef = useRef(null);
  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(pickVideoSrc);
  const skipVideo = false;
  const isFirstRender = useRef(true);

  const log = useCallback((msg) => {
    console.log(`[HeroVideo] ${msg}`);
  }, []);

  useLayoutEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl) {
      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.playsInline = true;
      log(`Layout: Muted & playsInline properties forced on element`);
    }
  }, [videoSrc, log]);

  // Update source dynamically on screen resize or orientation change
  useEffect(() => {
    if (typeof window === 'undefined') return;
    log(`Mount: screen=${window.innerWidth}x${window.innerHeight}, UA=${navigator.userAgent.substring(0, 100)}`);
    log(`Source: selected=${pickVideoSrc()}`);

    const media = window.matchMedia('(max-width: 767px)');
    const handleResize = () => {
      const nextSrc = pickVideoSrc();
      log(`Media change event: nextSrc=${nextSrc}`);
      setVideoSrc(nextSrc);
    };
    if (media.addEventListener) {
      media.addEventListener('change', handleResize);
    } else {
      media.addListener(handleResize);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handleResize);
      } else {
        media.removeListener(handleResize);
      }
    };
  }, [log]);

  const playSafe = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl || skipVideo) return;

    if (videoEl.paused) {
      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('webkit-playsinline', '');

      log(`PlaySafe: calling videoEl.play()`);
      const attempt = videoEl.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch((err) => {
          log(`PlaySafe Error: ${err.name} - ${err.message}`);
        });
      }
    } else {
      log(`PlaySafe: already playing state`);
    }
  }, [skipVideo, log]);

  useEffect(() => {
    if (skipVideo) return undefined;

    const videoEl = videoRef.current;
    if (!videoEl) return undefined;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      log(`FirstRender: let browser resolve original src`);
    } else {
      log(`UpdateRender: calling videoEl.load()`);
      videoEl.load();
    }

    const onReady = (e) => {
      log(`Event: "${e?.type}" fired.`);
      playSafe();
    };

    const onError = () => {
      const err = videoEl.error;
      log(`Event: "error" triggered. Code=${err?.code}, Message=${err?.message || 'none'}`);
    };

    videoEl.addEventListener('loadedmetadata', onReady);
    videoEl.addEventListener('canplay', onReady);
    videoEl.addEventListener('playing', onReady);
    videoEl.addEventListener('error', onError);
    videoEl.addEventListener('stalled', () => log('Event: "stalled" triggered'));
    videoEl.addEventListener('suspend', () => log('Event: "suspend" triggered'));
    videoEl.addEventListener('waiting', () => log('Event: "waiting" triggered'));
    videoEl.addEventListener('emptied', () => log('Event: "emptied" triggered'));

    if (videoEl.readyState >= 1) {
      log(`ReadyState on mount: ${videoEl.readyState} (calling playSafe)`);
      playSafe();
    }

    const onVisibility = () => {
      if (document.hidden) {
        log('Visibility: hidden (pausing video)');
        videoEl.pause();
      } else {
        log('Visibility: visible (resuming video)');
        playSafe();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const shell = document.querySelector('.app-shell');
    let shellObserver;
    if (shell) {
      const onShellReady = () => {
        if (shell.classList.contains('app-shell--ready')) {
          log('Shell: ready class detected (playing)');
          playSafe();
        }
      };
      onShellReady();
      shellObserver = new MutationObserver(onShellReady);
      shellObserver.observe(shell, { attributes: true, attributeFilter: ['class'] });
    }

    let intersectionObserver;
    const currentSection = sectionNodeRef.current;
    if (typeof IntersectionObserver === 'function' && currentSection) {
      intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            log('IntersectionObserver: visible (playing)');
            playSafe();
          }
        },
        { threshold: 0.1 },
      );
      intersectionObserver.observe(currentSection);
    }

    const unlockOnGesture = () => {
      log('Gesture: screen touchstart detected (playing)');
      playSafe();
    };
    currentSection?.addEventListener('touchstart', unlockOnGesture, { passive: true });
    window.addEventListener('touchstart', unlockOnGesture, { once: true, passive: true });

    const retryTimer = window.setInterval(() => {
      playSafe();
    }, 2500);
    const retryStop = window.setTimeout(() => window.clearInterval(retryTimer), 30000);

    return () => {
      videoEl.removeEventListener('loadedmetadata', onReady);
      videoEl.removeEventListener('canplay', onReady);
      videoEl.removeEventListener('playing', onReady);
      videoEl.removeEventListener('error', onError);
      document.removeEventListener('visibilitychange', onVisibility);
      shellObserver?.disconnect();
      intersectionObserver?.disconnect();
      currentSection?.removeEventListener('touchstart', unlockOnGesture);
      window.removeEventListener('touchstart', unlockOnGesture);
      window.clearInterval(retryTimer);
      window.clearTimeout(retryStop);
    };
  }, [skipVideo, playSafe, videoSrc, log]);

  return (
    <section
      ref={sectionNodeRef}
      className="home-hero-video"
      aria-label="Q-Tech introduction video"
    >
      <div className="home-hero-video__backdrop" aria-hidden="true" />
      <video
        key={videoSrc}
        ref={videoRef}
        className="hero-video-bg"
        src={videoSrc}
        autoPlay
        muted
        loop
        playsInline
        webkit-playsinline="true"
        preload="auto"
        disablePictureInPicture
        controls={false}
        poster={`${process.env.PUBLIC_URL}/assets/images/banner/banner-1.jpg`}
      />
    </section>
  );
};

export default HeroVideo;
